/**
 * <ActionProposalCard /> — Solomon Action Engine UI primitive.
 *
 * Renders a single PreparedAction as a confirm card. Used by the
 * Solomon panel, by manual action buttons, and (later) by the
 * workflow runner's "suggested follow-ups" surface.
 *
 * Behavior:
 *   - If `prepared.rejection` is set, the card renders a disabled
 *     state with the rejection message. No confirm button.
 *   - If `prepared.requiresConfirm` is true, the card shows
 *     Confirm + Dismiss buttons. Clicking Confirm calls
 *     `onConfirm(prepared)`.
 *   - If `requiresConfirm` is false, the card auto-fires once on
 *     mount via useEffect — useful when the proposal source already
 *     collected confirmation (e.g. a button click).
 *
 * The card does NOT manage execution state itself; the parent
 * passes `isExecuting` from useActionEngine() so a parent can
 * coordinate multiple cards.
 */

import { useEffect, useRef } from "react";
import { Sparkles, AlertTriangle, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { PreparedAction } from "@/lib/solomon-action-engine";

interface ActionProposalCardProps<P = Record<string, unknown>> {
  prepared: PreparedAction<P>;
  onConfirm: (prepared: PreparedAction<P>) => void;
  onDismiss?: () => void;
  isExecuting?: boolean;
  className?: string;
}

export function ActionProposalCard<P = Record<string, unknown>>({
  prepared,
  onConfirm,
  onDismiss,
  isExecuting,
  className,
}: ActionProposalCardProps<P>) {
  const autoFiredRef = useRef(false);
  const rejected = prepared.rejection !== null;
  const requiresConfirm = prepared.requiresConfirm;

  // Auto-fire low-stakes (no-confirm) actions exactly once. We
  // guard with a ref because StrictMode double-mounts effects in
  // dev, and we never want to fire twice.
  useEffect(() => {
    if (rejected) return;
    if (requiresConfirm) return;
    if (autoFiredRef.current) return;
    autoFiredRef.current = true;
    onConfirm(prepared);
  }, [rejected, requiresConfirm, prepared, onConfirm]);

  if (rejected) {
    return (
      <Card className={cn("border-destructive/50", className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Can't run this action
          </CardTitle>
          <CardDescription className="text-xs">
            {prepared.rejection?.message}
          </CardDescription>
        </CardHeader>
        {onDismiss && (
          <CardFooter className="pt-0">
            <Button variant="ghost" size="sm" onClick={onDismiss}>
              Dismiss
            </Button>
          </CardFooter>
        )}
      </Card>
    );
  }

  // If we're auto-firing (no confirm), render a lightweight inline
  // shell — the toast from useActionEngine becomes the user-visible
  // result; we don't need a full card.
  if (!requiresConfirm) {
    return null;
  }

  const rationale = prepared.proposal.rationale;

  return (
    <Card className={cn("border-primary/30", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Sparkles className="h-4 w-4 text-primary" />
          {prepared.definition.label}
          <Badge variant="outline" className="ml-auto text-[10px]">
            {prepared.proposal.source ?? "manual"}
          </Badge>
        </CardTitle>
        <CardDescription className="text-sm text-foreground">
          {prepared.preview.summary}
        </CardDescription>
      </CardHeader>
      {(rationale || prepared.preview.detail) && (
        <CardContent className="pb-2 space-y-2 text-xs text-muted-foreground">
          {rationale && <p className="italic">"{rationale}"</p>}
          {prepared.preview.detail}
        </CardContent>
      )}
      <CardFooter className="gap-2 pt-2">
        <Button
          size="sm"
          onClick={() => onConfirm(prepared)}
          disabled={isExecuting}
        >
          <Check className="h-4 w-4 mr-1" />
          Confirm
        </Button>
        {onDismiss && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            disabled={isExecuting}
          >
            <X className="h-4 w-4 mr-1" />
            Dismiss
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
